/**
 * useTaskActionExecutors — bound executor functions for all task actions.
 *
 * Returns a stable object of callbacks that perform the actual operations
 * (DB writes, toasts, selection cleanup). Every surface (MoreMenu, context
 * menus, keyboard shortcuts) can call these without duplicating logic.
 *
 * Operations that need additional user input (schedule, deadline, move,
 * tags, set-status) trigger their respective picker via store actions.
 */

import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { INCOMPLETE_PART_LABELS, incompleteDuplicateParts } from "@/features/task-actions/duplicateWarning"
import {
  bulkUncomplete,
  bulkCancel,
  bulkSchedule,
  bulkDelete,
  duplicateTask,
  convertTaskToSection,
  convertTaskToProject,
  focusTask,
  unfocusTask,
  isTaskFocused,
  pinTask,
  unpinTask,
  isTaskPinned,
} from "@/db/operations"
import {
  db,
  StartAtType,
  StartAtSubType,
  ObjectStatus,
  ObjectType,
} from "@/db/schema"
import { useViewContext } from "@/app/stores/viewContext"
import { useClipboardStore } from "@/shared/stores/clipboardStore"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { setContainerStatuses } from "@/features/project-header/projectStatusRequest"
import { hapticMedium } from "@/shared/utils/platform/haptics"
import { writeClipboard } from "@/shared/utils/platform/clipboard"
import { shareContent, getAppUrl } from "@/shared/utils/platform/share"
import { formatContainerForClipboard } from "@/shared/utils/clipboard/formatContainerForClipboard"
import type { TaskActionExecutors, TaskActionExecutorOptions } from "./types"

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTaskActionExecutors(
  taskIds: string[],
  options?: TaskActionExecutorOptions,
): TaskActionExecutors {
  const { t } = useTranslation()
  const closeTask = options?.closeTask
  const count = taskIds.length

  // --- Status operations ---------------------------------------------------

  const complete = useCallback(() => {
    // Projects and lists in the selection are completed the container way (asking what to do with
    // their open to-dos); the rest goes through the pending-completion queue
    void setContainerStatuses(taskIds, ObjectStatus.Completed).then((rest) => {
      const pendingAdd = usePendingCompletions.getState().add
      for (const id of rest) pendingAdd(id, "complete")
    })
    hapticMedium()
    useViewContext.getState().clearSelection()
    toast(
      count === 1 ? t("Completed") : t("Completed {{count}} tasks", { count }),
    )
  }, [taskIds, count, t])

  const uncomplete = useCallback(() => {
    void setContainerStatuses(taskIds, ObjectStatus.Unfinished).then((rest) => bulkUncomplete(rest))
    useViewContext.getState().clearSelection()
    toast(
      count === 1 ? t("Reopened") : t("Reopened {{count}} tasks", { count }),
    )
  }, [taskIds, count, t])

  const cancel = useCallback(() => {
    void setContainerStatuses(taskIds, ObjectStatus.Canceled).then((rest) => bulkCancel(rest))
    useViewContext.getState().clearSelection()
    toast(
      count === 1 ? t("Cancelled") : t("Cancelled {{count}} tasks", { count }),
    )
  }, [taskIds, count, t])

  // --- Schedule operations -------------------------------------------------

  const schedule = useCallback(() => {
    useViewContext.getState().openSchedulePicker(taskIds)
  }, [taskIds])

  const evening = useCallback(() => {
    const today = new Date().toISOString().split("T")[0]!
    void bulkSchedule(
      taskIds,
      today,
      StartAtType.Default,
      StartAtSubType.ThisEvening,
    )
    useViewContext.getState().clearSelection()
    toast(
      count === 1
        ? t("Scheduled for this evening")
        : t("Scheduled {{count}} tasks for this evening", { count }),
    )
  }, [taskIds, count, t])

  const someday = useCallback(() => {
    void bulkSchedule(taskIds, undefined, StartAtType.Someday)
    useViewContext.getState().clearSelection()
    toast(
      count === 1
        ? t("Moved to Someday")
        : t("Moved {{count}} tasks to Someday", { count }),
    )
  }, [taskIds, count, t])

  const deadline = useCallback(() => {
    useViewContext.getState().openDeadlinePicker(taskIds)
  }, [taskIds])

  // --- Focus & Pin operations ----------------------------------------------

  const focus = useCallback(() => {
    if (taskIds.length !== 1) return
    const id = taskIds[0]!
    void (async () => {
      const focused = await isTaskFocused(id)
      if (focused) {
        await unfocusTask(id)
        toast(t("Removed from focus"))
      } else {
        const result = await focusTask(id)
        if (result.ok) {
          toast(t("Added to focus"))
        } else {
          toast.error(result.error)
        }
      }
    })()
  }, [taskIds, t])

  const pin = useCallback(() => {
    if (taskIds.length !== 1) return
    const id = taskIds[0]!
    const containerId = useViewContext.getState().containerId
    if (!containerId) return
    void (async () => {
      const pinned = await isTaskPinned(containerId, id)
      if (pinned) {
        await unpinTask(containerId, id)
        toast(t("Unpinned"))
      } else {
        await pinTask(containerId, id)
        toast(t("Pinned to top"))
      }
    })()
  }, [taskIds, t])

  // --- Organize operations -------------------------------------------------

  const duplicate = useCallback(() => {
    void Promise.all(taskIds.map((id) => duplicateTask(id))).then((results) => {
      // The copies exist, but part of a task's content (files, document) may not have been copied
      const parts = incompleteDuplicateParts(results)
      if (parts.length > 0) {
        toast.warning(
          t("Some content was not copied to the duplicate ({{parts}}). Check the copy.", {
            parts: parts.map((p) => t(INCOMPLETE_PART_LABELS[p] ?? p)).join(", "),
          }),
        )
      }
    })
    useViewContext.getState().clearSelection()
    toast(t("Duplicated {{count}} tasks", { count }))
  }, [taskIds, count, t])

  const convertToSection = useCallback(() => {
    if (taskIds.length === 1) {
      void convertTaskToSection(taskIds[0]!)
      useViewContext.getState().clearSelection()
      closeTask?.()
      toast(t("Converted to section"))
    }
  }, [taskIds, closeTask, t])

  const convertToProject = useCallback(() => {
    if (taskIds.length === 1) {
      void convertTaskToProject(taskIds[0]!).then((result) => {
        if (result.ok) {
          useViewContext.getState().clearSelection()
          closeTask?.()
          toast(t("Converted to project"))
        }
      })
    }
  }, [taskIds, closeTask, t])

  // --- Clipboard operations ------------------------------------------------

  /**
   * Rich copy — full markdown with notes, checklist, tags, deadline.
   * Used by "Copy to Clipboard" menu item (for export / sharing).
   * Different from quickCopy which is for internal clipboard + titles.
   */
  const copyToClipboard = useCallback(() => {
    void (async () => {
      const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))
      const validTasks = tasks.filter(
        (task): task is NonNullable<typeof task> => task != null,
      )
      if (validTasks.length === 0) return

      const parts: string[] = []
      for (const task of validTasks) {
        // Container (Project/List): use dedicated formatter with children
        if (
          task.id &&
          (task.type === ObjectType.Project || task.type === ObjectType.List)
        ) {
          const containerText = await formatContainerForClipboard(task.id)
          if (containerText) {
            parts.push(containerText)
            continue
          }
        }

        const lines: string[] = []

        // Title with status prefix
        if (task.type === ObjectType.Section) {
          lines.push(`## ${task.title}`)
        } else if (task.status === ObjectStatus.Completed) {
          lines.push(`- [x] ${task.title}`)
        } else if (task.status === ObjectStatus.Canceled) {
          lines.push(`- [~] ${task.title}`)
        } else {
          lines.push(`- [ ] ${task.title}`)
        }

        // Notes — extract plain text from HTML
        if (task.docHtml) {
          const plainText = task.docHtml
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim()
          if (plainText) lines.push("", plainText)
        }

        // Checklist items
        try {
          const parentPath = `${task.parentPath}/${task.id}`
          const checklistItems = await db.totodo
            .where("[parentPath+type]")
            .equals([parentPath, ObjectType.Checklist])
            .filter((item) => item.isDeleted === 0)
            .sortBy("index")
          if (checklistItems.length > 0) {
            lines.push("")
            for (const item of checklistItems) {
              const prefix =
                item.status === ObjectStatus.Completed
                  ? "  - [x] "
                  : item.status === ObjectStatus.Canceled
                    ? "  - [~] "
                    : "  - [ ] "
              lines.push(prefix + item.title)
            }
          }
        } catch {
          // Non-critical — skip checklist on error
        }

        // Tags
        if (task.tags && task.tags.length > 0) {
          lines.push("", `${t("Tags")}: ${task.tags.join(", ")}`)
        }

        // Deadline
        if (task.dueAt) {
          lines.push(`${t("Deadline")}: ${task.dueAt}`)
        }

        parts.push(lines.join("\n"))
      }

      const text = parts.join("\n\n")
      await writeClipboard(text)
      toast(
        count === 1
          ? t("Copied to clipboard")
          : t("Copied {{count}} tasks", { count }),
      )
    })()
    useViewContext.getState().clearSelection()
  }, [taskIds, count, t])

  /**
   * Copy link — stable deep link to task (totodo.app/task/:id).
   * Works like Linear's "Copy link" — clipboard receives a URL that
   * resolves to the correct view regardless of where the task lives.
   */
  const copyLink = useCallback(() => {
    if (taskIds.length === 0) return
    const taskId = taskIds[0]
    const url = getAppUrl(`/task/${taskId}`)
    void writeClipboard(url).then(() => {
      toast(t("Link copied"))
    })
  }, [taskIds, t])

  /**
   * Quick copy — titles with status prefix to system clipboard + internal
   * clipboard reference. Used by Mod+C keyboard shortcut.
   */

  const quickCopy = useCallback(() => {
    const viewId = useViewContext.getState().viewId
    useClipboardStore.getState().set(taskIds, "copy", viewId)
    void (async () => {
      const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))
      const lines = tasks
        .filter((task): task is NonNullable<typeof task> => task != null)
        .map((task) => {
          if (task.type === ObjectType.Section) return `## ${task.title}`
          if (task.status === ObjectStatus.Completed)
            return `- [x] ${task.title}`
          if (task.status === ObjectStatus.Canceled)
            return `- [~] ${task.title}`
          return `# ${task.title}`
        })
        .join("\n")
      await writeClipboard(lines)
      toast(
        count === 1
          ? t("Copied to clipboard")
          : t("Copied {{count}} tasks", { count }),
      )
    })()
    useViewContext.getState().clearSelection()
  }, [taskIds, count, t])

  /** Cut — titles to clipboard + internal cut reference */
  const cut = useCallback(() => {
    const viewId = useViewContext.getState().viewId
    useClipboardStore.getState().set(taskIds, "cut", viewId)
    void (async () => {
      const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))
      const titles = tasks
        .filter((task): task is NonNullable<typeof task> => task != null)
        .map((task) => task.title)
        .join("\n")
      await writeClipboard(titles)
      toast(count === 1 ? t("Cut") : t("Cut {{count}} tasks", { count }))
    })()
  }, [taskIds, count, t])

  /**
   * Share — native share sheet or clipboard fallback.
   * Rich text with notes, checklist, tags, deadline, deep link.
   */
  const share = useCallback(async () => {
    if (taskIds.length === 0) return

    const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))
    const validTasks = tasks.filter(
      (task): task is NonNullable<typeof task> => task != null,
    )
    if (validTasks.length === 0) return

    const parts: string[] = []
    for (const task of validTasks) {
      const lines: string[] = [`# ${task.title}`]

      // Notes
      if (task.docHtml) {
        const plainText = task.docHtml
          .replace(/<[^>]*>/g, " ")
          .replace(/\s+/g, " ")
          .trim()
        if (plainText) lines.push("", plainText)
      }

      // Checklist items
      try {
        const parentPath = `${task.parentPath}/${task.id}`
        const checklistItems = await db.totodo
          .where("[parentPath+type]")
          .equals([parentPath, ObjectType.Checklist])
          .filter((item) => item.isDeleted === 0)
          .sortBy("index")
        if (checklistItems.length > 0) {
          lines.push("")
          for (const item of checklistItems) {
            const prefix =
              item.status === ObjectStatus.Completed
                ? "- [x] "
                : item.status === ObjectStatus.Canceled
                  ? "- [~] "
                  : "- [ ] "
            lines.push(prefix + item.title)
          }
        }
      } catch {
        // Non-critical — skip checklist on error
      }

      // Tags
      if (task.tags && task.tags.length > 0) {
        lines.push("", `${t("Tags")}: ${task.tags.join(", ")}`)
      }

      // Deadline
      if (task.dueAt) {
        lines.push(`${t("Deadline")}: ${task.dueAt}`)
      }

      // Stable deep link — works regardless of which view the task is in
      const url = getAppUrl(`/task/${task.id}`)
      lines.push("", url)

      parts.push(lines.join("\n"))
    }

    const text = parts.join("\n---\n")
    const title = validTasks[0]?.title ?? ""

    const result = await shareContent({ title, text })
    if (!result.shared && result.copiedToClipboard) {
      toast(t("Copied to clipboard"))
    }
  }, [taskIds, t])

  // --- Destructive operations ----------------------------------------------

  const deleteTasks = useCallback(async () => {
    await bulkDelete(taskIds)
    useViewContext.getState().clearSelection()
  }, [taskIds])

  // --- Return stable executors object --------------------------------------

  return useMemo(
    () => ({
      complete,
      uncomplete,
      cancel,
      schedule,
      evening,
      someday,
      deadline,
      focus,
      pin,
      duplicate,
      copyToClipboard,
      copyLink,
      quickCopy,
      cut,
      share,
      convertToSection,
      convertToProject,
      delete: deleteTasks,
    }),
    [
      complete,
      uncomplete,
      cancel,
      schedule,
      evening,
      someday,
      deadline,
      focus,
      pin,
      duplicate,
      copyToClipboard,
      copyLink,
      quickCopy,
      cut,
      share,
      convertToSection,
      convertToProject,
      deleteTasks,
    ],
  )
}
